import React, { useEffect, useRef } from 'react';
import {
  Alert,
  Button,
  Dropdown,
  Input,
  Modal,
  TextArea,
  Toggle,
  useToasts,
} from 'freemium-ui';
import { yupResolver } from '@hookform/resolvers/yup';
import { get, isArray, snakeCase, startCase, uniq } from 'lodash';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { Prompt } from 'react-router-dom';
import { v4 as uuid } from 'uuid';
import { ScrollContainer } from 'routes/components/styles';
import { FREEMIUM_DROPDOWN_TYPES, fieldTypes } from 'shared/constants';
import {
  CustomFieldType,
  FieldChoicesType,
  FormServFieldType,
  SaveContactFormFieldType,
} from 'shared/types';
import Icon from '../../../../../../shared/components/Icon';
import Reorder from '../../../../../../shared/components/Reorder';
import { useSaveContactFormField } from '../../../../../../shared/hooks';
import { getText } from '../../../../../../shared/text';
import {
  UUID_LENGTH_DD_CHOICES_FORMSERV,
  addNewFieldToHidden,
  getBulkChoicesSchema,
  getFormFieldDefaultValues,
  getFormFieldSchema,
} from '../utils';
import {
  BulkAddChoices,
  CustomFieldForm,
  CustomFieldFormActions,
  CustomFieldListItem,
  CustomFieldMeta,
  DropDownFieldForm,
  FieldChoice,
} from '../styles';
import {
  CUSTOM_CHOICE_LIMIT,
  DEFAULT_MANDATORY_FIELDS,
  hideContactRemoveOptionsFields,
} from '../constants';

const {
  settings: { customfields },
} = getText('EN');

type FieldEditType = {
  field: FormServFieldType;
  fields: FormServFieldType[];
  isLoading?: boolean;
  onDeleteField: (field: FormServFieldType, skipConfirmation?: boolean) => void;
  removeFieldFromHidden: (field: FormServFieldType) => void;
  setEditId: (editId: string) => void;
};

const FieldEdit = (props: FieldEditType) => {
  const { t, i18n } = useTranslation();
  const {
    field,
    fields,
    isLoading: isParentLoading,
    setEditId,
    removeFieldFromHidden,
    onDeleteField,
  } = props;
  const [, setToast] = useToasts();
  const scrollRef: any = useRef();

  const [showBulkEdit, toggleBulkEdit] = React.useState<boolean>(false);
  const [editChoiceId, setEditChoiceId] = React.useState('');

  const { mutate: saveContactField, isLoading: isSavingContactField } =
    useSaveContactFormField({
      setToast,
      callback: (
        isSuccess: boolean,
        response: FormServFieldType,
        variables: SaveContactFormFieldType,
      ) => {
        if (isSuccess) {
          setEditId('');
          // Only if the new field should be hidden from the table
          if (!variables.payload.id) addNewFieldToHidden(response);
        }
      },
    });

  const form = useForm<FormServFieldType>({
    shouldFocusError: true,
    mode: 'all',
    resolver: (formValues: FormServFieldType, ...resolverProps) => {
      return yupResolver(getFormFieldSchema({ fields, formValues }))(
        formValues,
        ...resolverProps,
      );
    },
    defaultValues: getFormFieldDefaultValues(),
  });

  const bulkChoicesForm = useForm<{ value: string }>({
    shouldFocusError: true,
    mode: 'all',
    resolver: (formValues: { value: string }, ...resolverProps) => {
      return yupResolver(getBulkChoicesSchema())(formValues, ...resolverProps);
    },
    defaultValues: {
      value: '',
    },
  });

  const {
    formState: { errors: bulkErrors },
  } = bulkChoicesForm;

  const {
    register,
    handleSubmit,
    reset,
    watch,
    setValue,
    formState: { submitCount, errors, touchedFields, isDirty },
  } = form;

  const allChoices = watch('choices') || [];
  const choices = allChoices?.map((c) => {
    return {
      ...c,
      value: i18n.exists(
        `contacts.import_contact.form_choices.language.${c.id}`,
      )
        ? `${t(`contacts.import_contact.form_choices.language.${c.id}`)}`
        : c.value,
    };
  });

  const isLimitExceed = choices.length < CUSTOM_CHOICE_LIMIT;

  const onSubmit = (data: FormServFieldType) => {
    saveContactField({
      payload: data,
      id: data.created_at ? data.id : undefined,
    });
  };

  const setFormValue = (fieldId: any, fieldValue: any) => {
    setValue(fieldId, fieldValue, {
      shouldValidate: true,
      shouldDirty: true,
      shouldTouch: true,
    });
  };

  const onFieldTypeChange = (option: CustomFieldType) => {
    if (!option) return;
    setFormValue('type', option.type);
    let choices: FieldChoicesType[] = [];
    if (FREEMIUM_DROPDOWN_TYPES.includes(option.type)) {
      choices = [
        {
          id: uuid().slice(0, UUID_LENGTH_DD_CHOICES_FORMSERV),
          value: `${t(`${customfields?.fields?.newOption?.name}`)}`,
        },
      ];
    }
    setFormValue('choices', choices);
  };

  const onEditChoice = (choiceId: string, value: string) => {
    setFormValue(
      'choices',
      choices?.map((choice) =>
        choiceId === choice.id ? { ...choice, value } : choice,
      ),
    );
  };

  const onDeleteChoice = (choiceId: string) => {
    setFormValue(
      'choices',
      choices?.filter((choice) => choice.id !== choiceId),
    );
  };

  const onAddNewChoice = (val?: string) => {
    const id = uuid().slice(0, UUID_LENGTH_DD_CHOICES_FORMSERV);
    const newChoices = [
      ...(watch('choices') || []),
      {
        id: id,
        value: val || '',
      },
    ];
    setEditChoiceId(id);
    setFormValue('choices', newChoices);
  };

  const onAddBulkChoices = ({ value: stringChoices }) => {
    const allChoices = uniq(
      stringChoices?.split('\n').filter((a: string) => a !== '') || [],
    );
    setFormValue('choices', []);
    allChoices.forEach((choice: string | any) => {
      if (choice) onAddNewChoice(choice);
    });
    toggleBulkEdit(false);
    bulkChoicesForm.reset({ value: '' });
  };

  const isLoading = isParentLoading || isSavingContactField;

  const created_at = watch('created_at');

  useEffect(() => {
    if (field.id) {
      reset(field);
    }
  }, [field]);

  useEffect(() => {
    setTimeout(() => {
      if (scrollRef && scrollRef.current) {
        scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
      }
    }, 100);
  }, [choices]);

  return (
    <CustomFieldListItem>
      <Prompt
        when={isDirty}
        message={t(`${customfields?.layout?.unload}`) || ''}
      />
      <CustomFieldForm>
        <CustomFieldMeta>
          <div className="label-text">
            {t('settings.custom_fields.display_label')}
          </div>
          <Input
            inline
            // label="Display label"
            disabled={
              field.field_options?.fs_default
                ? field.field_options?.fs_default
                : false
            }
            errorText={errors?.label?.message}
            isErrored={Boolean(submitCount > 0 && errors.label?.message)}
            placeholder={`${t('contacts.name_for_field')}`}
            {...register('label', {
              onChange: (event) => {
                if (!created_at && !touchedFields.name) {
                  setValue('name', snakeCase(event.target.value));
                }
              },
            })}
          />
          <div className="label-text">{t('forms.labels.field_name')}</div>
          <Input
            inline
            // label="Field name"
            disabled={Boolean(field.created_at) || field.name === 'language'}
            errorText={`${t(`${errors?.name?.message}`, {
              field: 'Name',
            })}`}
            isErrored={Boolean(submitCount > 0 && errors.name?.message)}
            {...register('name')}
          />
          <div className="spacer" />
          {!watch('field_options.fs_default') && (
            <>
              <div className="label-text">
                {t(`${customfields?.fields?.fieldType?.name}`)}
              </div>
              <Dropdown
                {...register('type')}
                isRequired
                emptyItemsText={`${t('no_results_found')}`}
                // label={customfields?.fields?.fieldType?.name}
                width="120px"
                disabled={field.name === 'language'}
                itemToString={(v: any) => `${t(v.name)}`}
                itemComponent={(v: any) => `${t(v.name)}`}
                items={fieldTypes}
                errorText={errors?.type?.message}
                isErrored={Boolean(submitCount > 0 && errors.type?.message)}
                defaultSelectedItem={
                  fieldTypes.find((fT) => watch('type') === fT.type) || ''
                }
                onChange={onFieldTypeChange}
              />
            </>
          )}
          {/* <div className="label-text">Filterable</div>
          <Toggle
            {...register('field_options.filterable')}
            on={watch('field_options.filterable')}
            disabled={field.name === 'language'}
            hasIcon={false}
            handleChange={() =>
              setFormValue(
                'field_options.filterable',
                !watch('field_options.filterable'),
              )
            }
          />
          <div className="label-text">Editable</div>
          <Toggle
            {...register('editable')}
            on={watch('editable')}
            disabled={field.name === 'language'}
            hasIcon={false}
            handleChange={() => setFormValue('editable', !watch('editable'))}
          />
          <div className="label-text">Custom</div>
          <Toggle
            {...register('custom')}
            on={watch('custom')}
            disabled={field.name === 'language'}
            hasIcon={false}
            handleChange={() => setFormValue('custom', !watch('custom'))}
          /> */}
          <div className="label-text" id="required-label">
            {startCase(t(`${customfields?.fields?.required?.name}`) || '')}
          </div>
          <Toggle
            {...{ 'aria-labelledby': 'required-label' }}
            {...register('required')}
            on={watch('required')}
            disabled={DEFAULT_MANDATORY_FIELDS.includes(field.name)}
            hasIcon={false}
            handleChange={() => setFormValue('required', !watch('required'))}
          />
        </CustomFieldMeta>
        {watch('type') === 2 && (
          <DropDownFieldForm>
            <div className="wrapper-div">
              <div>
                <Icon glyph="info" width="12" height="12" />
              </div>
              <div className="description">
                {t('settings.custom_fields.max_choice_limit', {
                  count: CUSTOM_CHOICE_LIMIT,
                })}
              </div>
            </div>
            {!isArray(errors.choices) && (
              <Alert>{errors.choices?.message}</Alert>
            )}
            <ScrollContainer ref={scrollRef} className="reorder-choices">
              {choices && choices.length > 0 && (
                <Reorder
                  onReorder={(newChoices) =>
                    setFormValue(
                      'choices',
                      newChoices.map((f, ind) => ({
                        ...f,
                        position: ind + 1,
                      })),
                    )
                  }
                  disabled={field.name === 'language'}
                  // idIdentifier="id"
                  customRender={(choice, index) => (
                    <FieldChoice key={choice.id}>
                      <Input
                        inline
                        value={choice.value}
                        errorText={get(
                          errors,
                          ['choices', index, 'value', 'message'],
                          '',
                        )}
                        disabled={field.name === 'language'}
                        isErrored={Boolean(
                          submitCount > 0 &&
                            get(
                              errors,
                              ['choices', index, 'value', 'message'],
                              false,
                            ),
                        )}
                        autoFocus={choice.id === editChoiceId}
                        onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                          setEditChoiceId(choice.id);
                          onEditChoice(choice.id, e.target.value);
                        }}
                      />
                      {choices.length > 1 &&
                        !hideContactRemoveOptionsFields.includes(
                          field.name,
                        ) && (
                          <div
                            className="remove"
                            onClick={() => {
                              if (field.name === 'language') return;
                              onDeleteChoice(choice.id || '');
                            }}
                          >
                            <Icon glyph="minus" width="16" height="16" />
                          </div>
                        )}
                    </FieldChoice>
                  )}
                  items={choices}
                />
              )}
            </ScrollContainer>
            {field.name !== 'language' && (
              <div className={`button-actions ${!isLimitExceed && 'disabled'}`}>
                <FieldChoice
                  className="add"
                  onClick={() => {
                    if (isLimitExceed)
                      onAddNewChoice(`${t('option')} ${choices.length + 1}`);
                  }}
                >
                  <Icon glyph="plus" width="16" height="16" />
                  &nbsp;&nbsp;{t(`${customfields?.fields?.newChoice?.name}`)}
                </FieldChoice>
                <div className="divider" />
                <FieldChoice
                  className="add"
                  onClick={() => {
                    if (isLimitExceed) {
                      toggleBulkEdit(true);
                      const bulkValue = form
                        .getValues()
                        .choices?.map((choice) => choice.value)
                        .join('\n');
                      bulkChoicesForm.setValue('value', bulkValue || '');
                    }
                  }}
                >
                  <Icon glyph="plus" width="16" height="16" />
                  &nbsp;&nbsp;{t('settings.custom_fields.bulk_choices_label')}
                </FieldChoice>
              </div>
            )}
            <Modal
              isOpen={showBulkEdit}
              align="center"
              position="center"
              onDismiss={() => toggleBulkEdit(false)}
            >
              <BulkAddChoices>
                <div className="choice-head">
                  {t('settings.custom_fields.add_dropdown_bulk')}
                </div>
                <div className="choice-desc">
                  {t('settings.custom_fields.bulk_choice_desc')}
                </div>
                <TextArea
                  className="multi-line"
                  {...bulkChoicesForm.register('value')}
                  isErrored={Boolean(bulkErrors.value)}
                  errorText={bulkErrors.value?.message}
                />
                <div className="choice-actions">
                  <div className="spacer" />
                  <Button
                    inline
                    type="secondary"
                    onClick={(event: React.MouseEvent) => {
                      event.preventDefault();
                      toggleBulkEdit(false);
                      bulkChoicesForm.clearErrors();
                    }}
                  >
                    {t('cancel')}
                  </Button>
                  <Button
                    inline
                    htmlType="submit"
                    onClick={(event: React.MouseEvent) => {
                      event.preventDefault();
                      bulkChoicesForm.handleSubmit(onAddBulkChoices, (err) =>
                        console.log('ERR', err),
                      )();
                    }}
                  >
                    {`${t('add_options')}`}
                  </Button>
                </div>
              </BulkAddChoices>
            </Modal>
          </DropDownFieldForm>
        )}
        <CustomFieldFormActions>
          <Button
            size="mini"
            inline
            disabled={isLoading}
            onClick={(event: React.MouseEvent) => {
              event.preventDefault();
              handleSubmit(onSubmit, (err) => console.log('ERR', err))();
            }}
          >
            {t('save')}
          </Button>
          <Button
            size="mini"
            inline
            disabled={isLoading}
            type="secondary"
            onClick={(event: React.MouseEvent) => {
              event.preventDefault();
              reset(field);
            }}
          >
            {t(`${customfields?.fields?.buttonReset?.name}`)}
          </Button>
          <Button
            size="mini"
            inline
            type="secondary"
            disabled={isLoading}
            onClick={(event: React.MouseEvent) => {
              event.preventDefault();
              reset(field);
              setEditId('');
              if (!created_at) {
                onDeleteField(field, true);
                removeFieldFromHidden(field);
              }
            }}
          >
            {t('cancel')}
          </Button>
        </CustomFieldFormActions>
      </CustomFieldForm>
    </CustomFieldListItem>
  );
};

export default FieldEdit;
